import { splitEntity } from "shelving";
import { Icon, MediaImage, MenuButton } from "shared/react";
import { type GroupData, type MemberData, NON_MEMBER, type NonMemberData, isApprovedMember } from "shared/schema";
import { getMediaImageStyle } from "shared/util/style";
import { useMemberItem, useUsernameItem } from "../util/data.js";
import { useItem, useQuery } from "../util/db.js";
import { GroupJoinPanel } from "./GroupJoinPanel.js";
import { GroupMenu } from "./GroupMenu.js";
import { MemberStatusBadge } from "./MemberStatusBadge.js";
import { RawMessageSentence } from "./MessageSentence.js";

export function RawGroupCard({ gid, group, member = NON_MEMBER }: { gid?: string; group: GroupData; member?: MemberData | NonMemberData | undefined }): React.ReactElement {
	const { name, photo, cover, hue, username } = group;
	const { status } = member;
	const path = gid ? (username ? `/${username}` : `/group/${gid}`) : undefined;
	return (
		<article className="card pad-none targeted" style={getMediaImageStyle(cover, hue)}>
			<div className="flex-row">
				<figure className="photo size-4x">{photo ? <MediaImage path={photo} /> : <Icon icon="chat" />}</figure>
				<div className="pad-xsmall text-left flex-full">
					<h3 className="space-xxsmall text-ellipsis">
						<a href={path} className="target">
							{name}
						</a>
					</h3>
					<p className="space-xxsmall flex-row gap-xsmall flex-left">
						<MemberStatusBadge status={status} />
					</p>
				</div>
			</div>
			{gid ? <MenuButton menu={<GroupMenu gid={gid} />} mods="button-quiet pin-top pin-right" /> : null}
			{gid && isApprovedMember(status) ? <LatestMessagePanel gid={gid} /> : null}
			{gid ? <GroupJoinPanel gid={gid} group={group} member={member} /> : null}
		</article>
	);
}

function LatestMessagePanel({ gid, thread = null }: { gid: string; thread?: string | null | undefined }): React.ReactElement | null {
	const query = useQuery("message", { gid, thread, $order: "!date", $limit: 1 });
	if (query.loading) return null;
	return <section className="panel pad-xsmall size-small text-ellipsis">{query.count ? <RawMessageSentence {...query.first} /> : "No messages"}</section>;
}

export function GroupCard({ gid }: { gid: string }): React.ReactElement {
	const group = useItem("group", gid);
	const member = useMemberItem(gid);
	return <RawGroupCard gid={gid} group={group.data} member={member.value} />;
}

export function UsernameGroupCard({ username }: { username: string }): React.ReactElement | null {
	const store = useUsernameItem(username);
	if (store.exists) {
		const [type, id] = splitEntity(store.data.creator);
		if (type === "group") return <GroupCard gid={id} />;
	}
	return null;
}
